import React, { useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import uniqBy from 'lodash/uniqBy'

import UploadInput from './UploadInput'
import { Modal } from 'components/Modal'
import { DataTable } from 'components/DataTable'
import { Select, Checkbox } from 'components/forms'
import { useMutation } from 'components/graphql'
import createRangeLoyaltyCards from '../../gql/createRangeLoyaltyCards'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { ErrorIco } from 'media/icons'
import dbColsOptions from './dbColsOptions'

import { BtnsWrapper, ErrorsWrapper } from './styled'
import styles from './CardImport.module.scss'

const CardImport = props => {
	const [fileTableCols, setFileTableCols] = useState([])
	const [fileTableData, setFileTableData] = useState([])
	const [colsConnection, setColsConnection] = useState([])
	const [useFirstFileRow, setUseFirstFileRow] = useState(true)
	const [validateError, setValidateError] = useState('')
	const [createRange] = useMutation(createRangeLoyaltyCards)
	const [t] = useTranslation()

	const getAccessorByHeader = Header => {
		const column = fileTableCols.find(col => col.Header === Header) || {}
		return column.accessor
	}
	const handleSelectConnection = (option, fileColName) => {
		const fileColAccesor = getAccessorByHeader(fileColName)
		const oldConnections = [...colsConnection]
		// Handle clear Select value
		if(!option) {
			oldConnections.some(({ fileCol }, i) => {
				if(fileCol === fileColName) {
					oldConnections.splice(i, 1)
					return true
				}
				return false
			})
			setColsConnection(oldConnections)
			return
		}
		const { value } = option
		const isUpdate = oldConnections.some(({ fileCol }, i) => {
			if(fileCol === fileColAccesor) {
				oldConnections[i] = { fileCol: fileColAccesor, dbCol: value }
				return true
			}
			return false
		})
		const newConnections = isUpdate ? oldConnections : [...oldConnections, { fileCol: fileColAccesor, dbCol: value }]
		setColsConnection(newConnections)
	}
	const handleUpload = rows => {
		const tableCols = rows[0].map((cellVal, i) => {
			const defaultColName = `${t('common.strings.column')} ${i + 1}`
			const Header = useFirstFileRow ? cellVal || defaultColName : defaultColName
			const accessor = useFirstFileRow ? cellVal || `${i}` : `${i}`
			return ({ Header, accessor, show: !!cellVal })
		})
		const tableData = rows.map(row => row.reduce((outObj, colVal, i) => {
			const accessor = tableCols[i].accessor
			outObj[accessor] = colVal
			return outObj
		}, {}))
		if(tableCols.length) {
			const defaultConnection = { fileCol: tableCols[0].accessor, dbCol: 'id' }
			setColsConnection([defaultConnection])
		}
		setFileTableCols(tableCols)
		setFileTableData(tableData)
		removeValidateError()
	}
	const getSelectVal = colHeader => {
		const accessor = getAccessorByHeader(colHeader)
		const connection = colsConnection.find(({ fileCol }) => fileCol === accessor) || {}
		return connection.dbCol
	}
	const selectConnectionsCols = [
		{ Header: t('loyalty_card_section.strings.file_column'), accessor: 'fileCol' },
		{ Header: t('loyalty_card_section.strings.table_field'), accessor: 'tableCol', Cell: (row) => (
			<Select
				noForm
				value={getSelectVal(row.original.fileCol)}
				options={dbColsOptions}
				onChange={val => handleSelectConnection(val, row.original.fileCol)}
				isClearable
			/>) }
	]
	const connectionTableData = (Array.isArray(fileTableCols) && fileTableCols.length)
		? fileTableCols.map(({ Header }) => ({ fileCol: Header }))
		: []
	const isColsConnected = !!(Array.isArray(colsConnection) && colsConnection.length)
	const tableConnectionCols = fileTableCols.map(col => {
		const { Header, accessor } = col
		const connectionMatch = colsConnection.find(({ fileCol }) => fileCol === accessor)
		const { label: dbColLabel } = connectionMatch ? dbColsOptions.find(({ value }) => value === connectionMatch.dbCol) : { label: '' }
		return { ...col, ...connectionMatch ? { Header: `${dbColLabel} (${Header})` } : { show: false } }
	})
	const handleUseFirstRow = e => {
		const value = e.target.checked
		if(fileTableData.length) {
			setColsConnection([])
			const firstRow = fileTableData[0]
			const newTableCols = fileTableCols.map((col, i) => {
				const defaultColName = `${t('common.strings.column')} ${i + 1}`
				const Header = value ? firstRow[col.accessor] || defaultColName : defaultColName
				return { ...col, Header }
			})
			setFileTableCols(newTableCols)
		}
		setUseFirstFileRow(value)
		removeValidateError()
	}
	const displayedFileTableData = useFirstFileRow ? fileTableData.slice(1) : fileTableData
	const validate = (selectedConnection) => {
		if(!Array.isArray(colsConnection) || colsConnection.length < 2) {
			return 'Table fields "Intern" and "RFID" should be specified'
		}
		const uniqueConnections = uniqBy(colsConnection, 'dbCol')
		if(uniqueConnections.length !== colsConnection.length) {
			return 'Specified table fields should not have duplicates'
		}
		const countReqConnections = colsConnection.reduce((count, { dbCol }) => {
			if(dbCol === 'id' || dbCol === 'rfid') {
				// eslint-disable-next-line no-param-reassign, no-plusplus
				count++
			}
			return count
		}, 0)
		if(countReqConnections < 2) {
			return 'Table fields "Intern" and "RFID" should be specified'
		}
		return ''
	}
	const removeValidateError = () => {
		if(validateError) {
			setValidateError('')
		}
	}
	const handleImport = e => {
		let outData = displayedFileTableData.map(row => {
			const outRow = {}
			colsConnection.forEach(({ fileCol, dbCol }) => {
				outRow[dbCol] = row[fileCol]
			})
			return outRow
		})
		const validationError = validate()
		setValidateError(validationError)
		if(!validationError) {
			createRange({ variables: { importItems: outData } })
		}
	}

	return(
		<Modal {...props} width={1400} label={t('loyalty_card_section.strings.import_loyalty_cards')} className={styles.root}>
			<UploadInput onUpload={handleUpload} />
			<Checkbox
				noForm
				label={t('loyalty_card_section.fields.use_file_first_row')}
				checked={useFirstFileRow}
				onChange={handleUseFirstRow}
			/>
			<Divider visible />
			{validateError &&
				<Row>
					<Col xs={2}>
						<ErrorsWrapper>
							<ErrorIco />
							{validateError}
						</ErrorsWrapper>
					</Col>
				</Row>
			}
			<Row>
				<Col xs={2}>
					<DataTable
						data={connectionTableData}
						label={t('loyalty_card_section.strings.fields_config')}
						columns={selectConnectionsCols}
						height={480}
						className={styles['with-select']}
					/>
				</Col>
				<Col xs={6}>
					{(!!fileTableData.length) &&
						<DataTable
							data={displayedFileTableData}
							label={t('loyalty_card_section.strings.file_preview')}
							columns={fileTableCols}
							height={480}
						/>
					}
				</Col>
				<Col xs={4}>
					{isColsConnected &&
						<DataTable
							data={displayedFileTableData}
							columns={tableConnectionCols}
							label={t('common.strings.preview')}
							height={480}
						/>
					}
				</Col>
			</Row>
			<Divider height={60} />
			<BtnsWrapper>
				<Button
					text={t('common.buttons.abort')}
					status='cancelation'
					onClick={props.unmount}
				/>
				<Button
					text={t('common.strings.import')}
					onClick={handleImport}
					status={isColsConnected ? 'primary' : 'inactive'}
				/>
			</BtnsWrapper>
		</Modal>
	)
}

export default CardImport
